import Grid, {GridParams} from "core/grid/domain/grid";
import {UILOCATION} from "core/grid/component/gridComp";
import {observable, action} from "mobx";
import {EditedGridDataObject, IDomainObject} from "core/form/formUtils";
import {ID} from "core/metamodel/metaConstant";
import uiService from "core/common/service/uiService";
import {DomainAncestor, fieldInfo} from "core/common/domain/DomainAncestor";
import gridService from "core/grid/gridService";
import {EntityStatus} from "core/event/eventDomain";
import {SEVERITY} from "core/common/domain/commonDomain";


export class GridComposite implements Movable {
  blockName: string;
  groupMembers: Grid[];
  @observable $dirty: boolean;
  @observable label: string;

  constructor(blockName: string, label: string, ...groupMembers: Grid[]) {
    this.blockName = blockName;
    this.label = label;

    if (groupMembers.length < 1) {
      throw new Error("There must be at least 1 grid");
    }
    this.groupMembers = groupMembers;
  }

  public isEditable() {
    return false;
  }
  public isClientBlock() {
    return false;
  }

  public getBlockName() {
    return this.blockName;
  }

  @action search(dataObject: IDomainObject, refresh: boolean = true): Promise<any> {
    let promises: Promise<any>[] = this.groupMembers.map( grid => grid.search(dataObject, refresh));
    return Promise.all(promises);
  }

  get data() {
    return this.groupMembers[0].data;
  }

  set data(data: any) {
  }

}




export interface Movable {

  getBlockName: () => string;
  isEditable: () => boolean;
  isClientBlock: () => boolean;
  $dirty: boolean;
  search: (dataObject: IDomainObject, refresh: boolean) => Promise<any>;
  label: string;
  data: any;

  deletedRowIds?: Set<ID>;
  getEditedRows?: () => DomainAncestor[];
}




export class MovingBlocks {

  @observable blockArray: Movable[];
  hiddenBlocks: Movable[];
  standAloneBlockName: string;
  standAloneBlockIndex: number;
  standAloneBlock: Movable;

  /**
   * blockArray bez standAloneBlockName
   */
  prunedBlockArray: Movable[];

  @observable activeBlockTab: string;
  tabbedBlocks: boolean;


  constructor(standAloneBlockName: string, tabbedBlocks: boolean, activeBlockTab: string,  ...blocks: Movable[]) {
    if (blocks.length < 1) {
      throw new Error("There must be at least 1 grid");
    }

    this.blockArray = blocks;
    this.standAloneBlockName = standAloneBlockName;
    this.activeBlockTab = activeBlockTab;
    this.tabbedBlocks = tabbedBlocks;

    this.computeDerivedInfo();
    this.fixActiveTab();

    // kvuli chainum na subgridech
    for(let block of blocks) {
      if (block instanceof Grid) {
        gridService.gridMap.set(block.gridLocation, block);
      }
    }

  }



  private computeDerivedInfo() {
    this.standAloneBlockIndex = this.blockArray.findIndex(g => g.getBlockName() === this.standAloneBlockName);
    if (this.standAloneBlockName && this.standAloneBlockIndex === -1) {
      throw new Error("standAloneBlockName " + this.standAloneBlockName + " not found in blocks");
    }

    this.standAloneBlock = this.blockArray.find(g => g.getBlockName() === this.standAloneBlockName);
    this.prunedBlockArray = this.blockArray.filter(g => g.getBlockName() !== this.standAloneBlockName);
  }


  public reinit(standAloneBlockName: string, visibleBlockNames: string[]) {
    this.standAloneBlockName = standAloneBlockName;

    if (!this.hiddenBlocks) this.hiddenBlocks = [];

    // nejdriv pridame schovane
    for(let blockName of visibleBlockNames) {
      if (!this.getBlock(blockName)) {
        let i = this.hiddenBlocks.findIndex(b => b.getBlockName() === blockName);
        if (i >= 0) {
          this.blockArray.push(this.hiddenBlocks[i]);
          this.hiddenBlocks.splice(i, 1);
        }
      }
    }
    // potom schovame chybejici
    let newBlockArray: Movable[] = [];
    for(let block of this.blockArray) {
      let h = visibleBlockNames.indexOf(block.getBlockName());
      if (h === -1) {
        this.hiddenBlocks.push(block);
      } else {
        newBlockArray.push(block);
      }
    }
    this.blockArray = newBlockArray;


    console.log('MovingBlocks reinit: new visible blocks: %O' , this.blockArray.map(b => b.getBlockName()));

    this.computeDerivedInfo();
    this.fixActiveTab();
  }

  public fixActiveTab(): void {
    let arrayToUse: Movable[]  = (uiService.globalMediaQuery.matches) ? this.prunedBlockArray : this.blockArray;
    if (this.tabbedBlocks && !arrayToUse.find(b => b.getBlockName() === this.activeBlockTab)) {
      this.activeBlockTab = (arrayToUse.length) ? arrayToUse[0].getBlockName() : null;
      console.log("FIXING active tab to " + this.activeBlockTab);
    }
  }


  getBlock(blockName: string): Movable {
    return this.blockArray.find(g => g.getBlockName() === blockName);
  }

  getBlockFromPruned(blockName: string): Movable {
    return this.prunedBlockArray.find(g => g.getBlockName() === blockName);
  }


  getTabBlocks(): Movable[] {
    return (uiService.globalMediaQuery.matches) ? this.prunedBlockArray : this.blockArray;
  }

  getUiLocation(blockName: string): UILOCATION {
    return (uiService.globalMediaQuery.matches && this.standAloneBlockName === blockName) ? UILOCATION.main_right : UILOCATION.tab;
  }

  getEditedBlocks(): Movable[] {
    return this.blockArray.filter(g => this.isEditedBlock(g));
  }

  isEditedBlock(block: Movable): boolean {
    return block.isEditable() && block.$dirty;
  }

  getGridData(): EditedGridDataObject {
    return this.getEditedBlocks().reduce<EditedGridDataObject>((acc:EditedGridDataObject, block: Movable) => {
      acc[block.getBlockName()] = {
        deletedRowIds: (block.deletedRowIds) ? Array.from(block.deletedRowIds) : [],
        editedRows: block.getEditedRows()
      }
      return acc;
    }, {});
  }

  /**
   * Pokud isWide je true, vytrhne z gridCompArray standAloneGrid a vrati ho.  gridCompArray je zmeneno.
   * @param isWide
   * @param gridCompArray
   * @returns {*}
   */
  pluckStandAloneBlock(gridCompArray: any[]) {
    if (uiService.globalMediaQuery.matches) {
      let [standAloneGridComp] = gridCompArray.splice(this.standAloneBlockIndex, 1);
      return standAloneGridComp;
    }
    return null;
  }

  firstTabBlock() {
    return (uiService.globalMediaQuery.matches && this.standAloneBlockIndex === 0 && this.blockArray.length > 1) ? this.blockArray[1] : this.blockArray[0];
  }

  doOnExposedBlocks(callback: (block: Movable) => boolean) {
    if (this.blockArray.length === 1) {
      return callback(this.blockArray[0]);
    }

    // pokud neni nastaven  standAloneBlockName a activeBlockTab nejsou taby => zavolej na vsechny
    if (!this.standAloneBlockName && !this.activeBlockTab) {
      let res: boolean;
      for(let block of this.blockArray) {
        res = callback(block) && res;
      }
      return res;
    }

    if (uiService.globalMediaQuery.matches) {

      let secondBlock: Movable = this.getBlockFromPruned(this.activeBlockTab);
      if (!secondBlock) secondBlock = this.prunedBlockArray[0];

      let res1 = callback(this.standAloneBlock);
      let res2 = callback(secondBlock);

      return res1 || res2;

    } else {
      let firstBlock = this.getBlock(this.activeBlockTab);
      if (!firstBlock) firstBlock = this.blockArray[0];
      return callback(firstBlock);
    }
  }

  isExposed(blockName: string) {
    if (uiService.globalMediaQuery.matches) {
      if (this.standAloneBlockName === blockName) return true;
      let secondBlock = this.getBlockFromPruned(this.activeBlockTab);
      if (!secondBlock) secondBlock = this.prunedBlockArray[0];
      return (secondBlock && secondBlock.getBlockName() === blockName)
    } else {
      let firstBlock = this.getBlock(this.activeBlockTab);
      if (!firstBlock) firstBlock = this.blockArray[0];
      return (firstBlock && firstBlock.getBlockName() === blockName)
    }
  }

  searchExposedGrids(dataObject: IDomainObject, force: boolean = false) {
    this.doOnExposedBlocks((block) => {
      if (!block.data || force) {
        block.search(dataObject, true);
        return true;
      } else {
        return false;
      }
    });
  }

  cleanAfterSave(dataObject: IDomainObject): void {
    for(let block of this.blockArray) {
      if (this.isEditedBlock(block) || block.isClientBlock()) {
        block.deletedRowIds = new Set<ID>();
        block.$dirty = false;

        if (block.isClientBlock() || this.isExposed(block.getBlockName())) {
          block.search(dataObject, true);
        } else {
          block.data = null;
        }

      }
    }
  }

}

export class GridBulkEntityDto extends DomainAncestor {
  @observable @fieldInfo(EntityStatus) entityStatuses: EntityStatus[];
  @observable gridParams: GridParams;

  @observable part: number;
  @observable count: number;
  @observable showErrors: boolean;

  constructor(json: any) {
    super(json);
  }

  issuableCount() : number {
    return (this.entityStatuses && this.entityStatuses.length) ? this.entityStatuses.filter(cs => cs.status !== SEVERITY.ERROR).length : 0;
  }

}



// WEBPACK FOOTER //
// ./src/core/grid/gridUtils.ts